// Tranche 3 (white-label readiness) — per-tenant branding for the app shell.
//
// Fetches the signed-in user's tenant branding once (GET /tenants/current →
// effective_branding, already merged with deployment defaults server-side)
// and exposes it to the shell: app name, tagline, logo, primary color, and
// the terminology map that relabels the core entity nouns (Candidates →
// Applicants, …).
//
// Until the fetch resolves (or if it fails — e.g. a 'pending' user with no
// data access yet), the platform defaults below apply, which match the
// pre-white-label UI exactly.

import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import api from '../lib/api';

export interface Branding {
  app_name: string;
  company_name: string;
  tagline: string;
  logo_url: string | null;
  primary_color: string | null;
  terminology: Record<string, string>;
}

// [split W1-B] Pre-fetch / error fallback for the app shell. The FOUNDING
// (FNS) identity, env-overridable via VITE_APP_NAME / VITE_COMPANY_NAME: a
// signed-in tenant's real name arrives from GET /tenants/current
// (effective_branding, resolved server-side to the tenant's own values), so
// this only shows briefly before that resolves or if it fails. Keeping it the
// founding default (not a neutral placeholder) keeps FNS's authenticated shell
// byte-identical on load; a reseller frontend build overrides via the env vars.
const DEFAULTS: Branding = {
  app_name: (import.meta.env.VITE_APP_NAME as string | undefined)?.trim() || 'Boaz AI',
  company_name: (import.meta.env.VITE_COMPANY_NAME as string | undefined)?.trim() || 'Boaz AI',
  tagline: '',
  logo_url: null,
  primary_color: null,
  terminology: {},
};

interface BrandingContextValue {
  branding: Branding;
  /** Relabel a UI string using the tenant's terminology map — replaces the
   *  core entity nouns (Candidate/s, Job/s, Client/s, Placement/s,
   *  Recruiter/s) as whole words, preserving the rest of the label. */
  applyTerm: (label: string) => string;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: DEFAULTS,
  applyTerm: (l) => l,
});

// noun → terminology key, singular and plural. Whole-word, case-preserving
// for the common Title-case labels used across the nav.
const TERM_WORDS: Array<{ re: RegExp; key: string }> = [
  { re: /\bCandidates\b/g, key: 'candidates' },
  { re: /\bCandidate\b/g, key: 'candidate' },
  { re: /\bJobs\b/g, key: 'jobs' },
  { re: /\bJob\b/g, key: 'job' },
  { re: /\bClients\b/g, key: 'clients' },
  { re: /\bClient\b/g, key: 'client' },
  { re: /\bPlacements\b/g, key: 'placements' },
  { re: /\bPlacement\b/g, key: 'placement' },
  { re: /\bRecruiters\b/g, key: 'recruiters' },
  { re: /\bRecruiter\b/g, key: 'recruiter' },
];

export function applyTerminology(label: string, terminology: Record<string, string>): string {
  if (!label || Object.keys(terminology).length === 0) return label;
  let out = label;
  for (const { re, key } of TERM_WORDS) {
    const replacement = terminology[key];
    if (replacement) out = out.replace(re, replacement);
  }
  return out;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/tenants/current');
        const eb = r.data?.effective_branding;
        if (!cancelled && eb && typeof eb === 'object') {
          setBranding({
            app_name: eb.app_name || DEFAULTS.app_name,
            company_name: eb.company_name || DEFAULTS.company_name,
            tagline: eb.tagline ?? '',
            logo_url: eb.logo_url ?? null,
            primary_color: eb.primary_color ?? null,
            terminology: eb.terminology && typeof eb.terminology === 'object' ? eb.terminology : {},
          });
        }
      } catch {
        // Defaults are always a safe render; 'pending' users and transient
        // errors simply keep the platform look.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // document.title is owned per-route by usePageTitle() (lib/pageTitle.ts),
    // mounted in the Layout — so tabs/history stay navigable. Here we only
    // push the tenant's accent color into the CSS custom property that
    // globals.css themes off of.
    if (branding.primary_color) {
      document.documentElement.style.setProperty('--brand-primary', branding.primary_color);
    }
  }, [branding]);

  const value = useMemo<BrandingContextValue>(() => ({
    branding,
    applyTerm: (label: string) => applyTerminology(label, branding.terminology),
  }), [branding]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
